type VideoConstraints = {
  maxDuration: number
  maxFileSize: number
  maxWidth: number
  maxHeight: number
  requiredFormat: string
  requiredVideoCodec: string
  requiredAudioCodec: string
}

type ValidationResult = {
  isValid: boolean
  errors: string[]
}

export const useVideoValidation = () => {
  const validateVideo = async (
    file: File,
    constraints: VideoConstraints,
  ): Promise<ValidationResult> => {
    const errors: string[] = []

    if (!file.type.includes('video/mp4')) {
      errors.push('Video must be in MP4 format')
      return { isValid: false, errors }
    }

    if (file.size > constraints.maxFileSize) {
      const maxSizeMB = (constraints.maxFileSize / (1024 * 1024)).toFixed(1)
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1)
      errors.push(`File size ${fileSizeMB}MB exceeds maximum ${maxSizeMB}MB`)
    }

    const validationPromise = new Promise<void>((resolve) => {
      const video = document.createElement('video')
      video.preload = 'metadata'

      video.onloadedmetadata = () => {
        if (video.duration > constraints.maxDuration) {
          errors.push(
            `Video duration ${video.duration.toFixed(1)}s exceeds maximum ${constraints.maxDuration}s`,
          )
        }

        if (video.videoWidth > constraints.maxWidth) {
          errors.push(
            `Video width ${video.videoWidth}px exceeds maximum ${constraints.maxWidth}px`,
          )
        }

        if (video.videoHeight > constraints.maxHeight) {
          errors.push(
            `Video height ${video.videoHeight}px exceeds maximum ${constraints.maxHeight}px`,
          )
        }

        URL.revokeObjectURL(video.src)
        resolve()
      }

      video.onerror = () => {
        errors.push('Unable to load video file')
        URL.revokeObjectURL(video.src)
        resolve()
      }

      video.src = URL.createObjectURL(file)
    })

    await validationPromise

    return {
      isValid: errors.length === 0,
      errors,
    }
  }
  return {
    validateVideo,
  }
}

export const VIDEO_CONSTRAINTS = {
  desktop: {
    maxDuration: 10,
    maxFileSize: 3 * 1024 * 1024,
    maxWidth: 1920,
    maxHeight: 1080,
    requiredFormat: 'MP4',
    requiredVideoCodec: 'H.264',
    requiredAudioCodec: 'AAC',
  },
  mobile: {
    maxDuration: 10,
    maxFileSize: 3 * 1024 * 1024,
    maxWidth: 1080,
    maxHeight: 1920,
    requiredFormat: 'MP4',
    requiredVideoCodec: 'H.264',
    requiredAudioCodec: 'AAC',
  },
  vertical_desktop: {
    maxDuration: 10,
    maxFileSize: 3 * 1024 * 1024,
    maxWidth: 900,
    maxHeight: 1200,
    requiredFormat: 'MP4',
    requiredVideoCodec: 'H.264',
    requiredAudioCodec: 'AAC',
  },
  vertical_mobile: {
    maxDuration: 10,
    maxFileSize: 3 * 1024 * 1024,
    maxWidth: 600,
    maxHeight: 800,
    requiredFormat: 'MP4',
    requiredVideoCodec: 'H.264',
    requiredAudioCodec: 'AAC',
  },
} as const
